import { FC, Fragment, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  listGiftBrandSellerDeliveries,
  updateGiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.controller';
import {
  GiftDeliveryForAdmin,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { giftItemBrandListDataAtom } from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { useAsyncFn } from 'react-use';

import {
  nextStatusLabel,
  nextStatusMap,
  prevStatusLabel,
  prevStatusMap,
  statusLabel,
  typeLabel,
} from '../../giftSeller/utils/delivery.constants';
import {
  formatPhone,
  getItemName,
  getOrdererInfo,
} from '../../giftSeller/utils/delivery.utils';
import { GiftBrandThanksCardModal } from '../components/GiftBrandThanksCardModal';

// 송장 입력 모달 상태
// - mode 'advance': 준비중 -> 배송중 전환과 함께 송장 입력 (발송시각=현재)
// - mode 'edit'   : 이미 배송중/배송완료인 건의 송장만 수정 (상태/발송·완료시각 보존)
interface ShippingModalState {
  deliveryId: string;
  carrier: string;
  trackingNumber: string;
  mode: 'advance' | 'edit';
  // edit 모드에서 유지할 현재 상태 및 보존할 기존 시각 (ISO)
  status?: GiftDeliveryStatus;
  shippedAt?: string;
  deliveredAt?: string;
}

// 배송완료 처리 모달 상태 (배송중 -> 배송완료)
interface DeliverModalState {
  deliveryId: string;
  deliveredAt: string; // datetime-local 입력값 (YYYY-MM-DDTHH:mm)
}

// 모든 시각 표기/입력은 한국 시각(KST) 기준. (브라우저가 미국 등 타 타임존이어도 동일)
const KST = 'Asia/Seoul';

// Date -> 한국 시각(KST)의 연/월/일/시/분 구성요소 (문자열, zero-padded)
const kstParts = (date: Date) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: KST,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
  };
};

// datetime-local input 기본값: 현재 한국 시각 (YYYY-MM-DDTHH:mm)
const nowForInput = () => {
  const p = kstParts(new Date());
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

// datetime-local 입력값(한국 시각 벽시계)을 ISO(UTC) 문자열로 변환
const kstInputToISO = (value: string) =>
  new Date(`${value}:00+09:00`).toISOString();

// 연도 없이 월.일 시:분 + 상대일(오늘/어제/n일 전), KST 기준 (배송요청·완료일 공용)
const formatRelativeDateTime = (date: Date | string | undefined) => {
  if (!date) return null;
  const p = kstParts(new Date(date));
  const now = kstParts(new Date());

  // 달력(KST) 기준 일수 차이 (시:분 무시)
  const startOf = (q: ReturnType<typeof kstParts>) =>
    Date.UTC(Number(q.year), Number(q.month) - 1, Number(q.day));
  const diffDays = Math.round((startOf(now) - startOf(p)) / 86400000);
  let relative: string;
  if (diffDays <= 0) relative = '오늘';
  else if (diffDays === 1) relative = '어제';
  else relative = `${diffDays}일 전`;

  return {
    date: `${Number(p.month)}.${Number(p.day)}`,
    time: `${p.hour}:${p.minute}`,
    relative,
  };
};

// 발송/완료 시각: 연도 없이 월.일 시:분 (KST 기준, 배송요청 표기와 동일한 느낌)
const formatShortDateTime = (date: Date | string | undefined) => {
  if (!date) return null;
  const p = kstParts(new Date(date));
  return `${Number(p.month)}.${Number(p.day)} ${p.hour}:${p.minute}`;
};

// 필터 칩으로 노출되는 전체 상태 (배송완료 포함)
const FILTERABLE_STATUSES = [
  GiftDeliveryStatus.WaitingAddress,
  GiftDeliveryStatus.AddressSubmitted,
  GiftDeliveryStatus.Preparing,
  GiftDeliveryStatus.Shipping,
  GiftDeliveryStatus.Delivered,
  // 취소 건도 목록에 포함되어 감사카드 조회가 가능하도록 필터 노출
  GiftDeliveryStatus.Canceled,
];

// 기본 선택: 진행중 상태만. 주소입력대기는 배송지 미정이라 기본 제외(칩으로는 선택 가능)
const DEFAULT_STATUSES: GiftDeliveryStatus[] = [
  GiftDeliveryStatus.AddressSubmitted,
  GiftDeliveryStatus.Preparing,
  GiftDeliveryStatus.Shipping,
];

export const GiftBrandDeliveryManagePage: FC = function GiftBrandDeliveryManagePage() {
  // 여러 상태를 동시에 선택 가능 (다중 선택)
  const [statusFilter, setStatusFilter] =
    useState<GiftDeliveryStatus[]>(DEFAULT_STATUSES);
  const [searchText, setSearchText] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [revertingId, setRevertingId] = useState<string | null>(null);
  const [shippingModal, setShippingModal] = useState<ShippingModalState | null>(null);
  const [deliverModal, setDeliverModal] = useState<DeliverModalState | null>(null);
  const [cardDelivery, setCardDelivery] = useState<GiftDeliveryForAdmin | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Admin/GiftAgent는 전 브랜드(admin), 브랜드셀러는 본인 브랜드(gift-brand-seller)로 조회
  const { giftBrandRole } = useAuth();
  const role = giftBrandRole() as LowerRole.Admin | LowerRole.GiftBrandSeller;
  const isAdminView = role === LowerRole.Admin;

  // 감사카드 템플릿 (templateId 매칭용). Admin/GiftAgent는 전 브랜드, 셀러는 본인 브랜드.
  useGiftItemBrandListHook(role);
  const brands = useRecoilValue(giftItemBrandListDataAtom);
  const thanksTemplates = useMemo(
    () => brands?.[0]?.thanksPrint?.templates ?? [],
    [brands],
  );

  const [{ loading, value: deliveries }, fetchDeliveries] = useAsyncFn(
    async (statuses: GiftDeliveryStatus[]) => {
      // 선택된 상태가 없으면 빈 목록 (전체 이력 로드 방지)
      if (!statuses.length) return [];
      return listGiftBrandSellerDeliveries(
        { deliveryStatus: { $in: statuses } },
        { createdAt: -1 },
        role,
      );
    },
    [role],
  );

  useEffect(() => {
    fetchDeliveries(statusFilter);
  }, [fetchDeliveries, statusFilter]);

  const toggleStatus = (s: GiftDeliveryStatus) =>
    setStatusFilter((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );

  const copyAddress = async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      alert('복사에 실패했습니다.');
    }
  };

  const rows = useMemo(() => {
    // 상태 필터는 서버 조회(deliveryStatus $in)로 처리되므로 여기서는 검색만 적용
    let result = [...(deliveries ?? [])];
    if (searchText) {
      const search = searchText.toLowerCase().replace(/-/g, '');
      result = result.filter((d) => {
        const phone = d.recipient?.phoneNumber?.replace(/-/g, '') || '';
        const name = d.recipient?.name?.toLowerCase() || '';
        const item = getItemName(d).toLowerCase();
        return phone.includes(search) || name.includes(search) || item.includes(search);
      });
    }
    return result;
  }, [deliveries, searchText]);

  // 다음 단계로 상태 변경
  const advanceStatus = async (delivery: GiftDeliveryForAdmin) => {
    const next = nextStatusMap[delivery.deliveryStatus];
    if (!next) return;

    // 준비중 -> 배송중 은 송장 정보를 받는다
    if (next === GiftDeliveryStatus.Shipping) {
      setShippingModal({
        deliveryId: delivery._id,
        carrier: delivery.shipping?.carrier || '',
        trackingNumber: delivery.shipping?.trackingNumber || '',
        mode: 'advance',
      });
      return;
    }

    // 배송중 -> 배송완료 는 배송완료 일시를 입력받는다 (기본값: 현재 시각)
    if (next === GiftDeliveryStatus.Delivered) {
      setDeliverModal({
        deliveryId: delivery._id,
        deliveredAt: nowForInput(),
      });
      return;
    }

    setProcessingId(delivery._id);
    try {
      await updateGiftDeliveryStatus(delivery._id, next, undefined, role);
      await fetchDeliveries(statusFilter);
    } catch (err) {
      console.error(err);
      alert('상태 변경에 실패했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  // 이미 배송중/배송완료인 건의 송장만 추가/수정 (상태 전환 없음)
  const openShippingEdit = (delivery: GiftDeliveryForAdmin) => {
    setShippingModal({
      deliveryId: delivery._id,
      carrier: delivery.shipping?.carrier || '',
      trackingNumber: delivery.shipping?.trackingNumber || '',
      mode: 'edit',
      status: delivery.deliveryStatus,
      // 기존 발송/완료 시각을 보존 (shipping 서브도큐먼트 전체가 교체되므로)
      shippedAt: delivery.shipping?.shippedAt
        ? new Date(delivery.shipping.shippedAt).toISOString()
        : undefined,
      deliveredAt: delivery.shipping?.deliveredAt
        ? new Date(delivery.shipping.deliveredAt).toISOString()
        : undefined,
    });
  };

  const submitShipping = async () => {
    if (!shippingModal) return;
    const isEdit = shippingModal.mode === 'edit';
    // 수정 모드는 상태를 그대로 유지, 전환 모드는 배송중으로
    const targetStatus =
      isEdit && shippingModal.status
        ? shippingModal.status
        : GiftDeliveryStatus.Shipping;
    setProcessingId(shippingModal.deliveryId);
    try {
      await updateGiftDeliveryStatus(
        shippingModal.deliveryId,
        targetStatus,
        {
          carrier: shippingModal.carrier.trim() || undefined,
          trackingNumber: shippingModal.trackingNumber.trim() || undefined,
          // 전환 모드는 발송시각=현재, 수정 모드는 기존 시각 보존
          shippedAt: isEdit ? shippingModal.shippedAt : new Date().toISOString(),
          deliveredAt: isEdit ? shippingModal.deliveredAt : undefined,
        },
        role,
      );
      setShippingModal(null);
      await fetchDeliveries(statusFilter);
    } catch (err) {
      console.error(err);
      alert(isEdit ? '송장 수정에 실패했습니다.' : '송장 등록에 실패했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  const submitDeliver = async () => {
    if (!deliverModal) return;
    if (!deliverModal.deliveredAt) {
      alert('배송완료 일시를 입력해주세요.');
      return;
    }
    setProcessingId(deliverModal.deliveryId);
    try {
      await updateGiftDeliveryStatus(
        deliverModal.deliveryId,
        GiftDeliveryStatus.Delivered,
        { deliveredAt: kstInputToISO(deliverModal.deliveredAt) },
        role,
      );
      setDeliverModal(null);
      await fetchDeliveries(statusFilter);
    } catch (err) {
      console.error(err);
      alert('배송완료 처리에 실패했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  // 실수로 진행한 상태 변경을 이전 단계로 되돌린다 (admin / gift-brand-seller 공통)
  const revertStatus = async (delivery: GiftDeliveryForAdmin) => {
    const prev = prevStatusMap[delivery.deliveryStatus];
    if (!prev) return;
    const label = prevStatusLabel[delivery.deliveryStatus] || '이전 상태로 되돌리기';
    if (!confirm(`${label} 하시겠습니까?`)) return;
    setRevertingId(delivery._id);
    try {
      await updateGiftDeliveryStatus(delivery._id, prev, undefined, role);
      await fetchDeliveries(statusFilter);
    } catch (err) {
      console.error(err);
      alert('상태 되돌리기에 실패했습니다.');
    } finally {
      setRevertingId(null);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-bold">주문 배송관리</h2>
        <p className="mt-1 text-sm text-gray-500">
          신규 주문을 발주 → 배송 → 완료 단계로 처리하세요.
        </p>
      </div>

      {/* 필터 */}
      <div className="mb-4 space-y-2">
        {/* 상태 다중 선택 칩 */}
        <div className="flex flex-wrap items-center gap-1.5">
          {FILTERABLE_STATUSES.map((s) => {
            const active = statusFilter.includes(s);
            return (
              <button
                key={s}
                onClick={() => toggleStatus(s)}
                className={`rounded-full border px-2.5 py-1 text-xs font-medium transition ${
                  active
                    ? 'border-indigo-600 bg-indigo-600 text-white'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {statusLabel[s]?.text ?? s}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="수령인 · 연락처 · 상품명"
            className="flex-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm"
          />
          <button
            onClick={() => fetchDeliveries(statusFilter)}
            className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
          >
            새로고침
          </button>
        </div>
      </div>

      {loading ? (
        <div className="center-box py-16">
          <Loading />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
          {statusFilter.length === 0
            ? '조회할 상태를 선택해주세요.'
            : '처리할 배송 건이 없습니다.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-[11px] font-medium uppercase tracking-wide text-gray-400">
              <tr>
                <th className="px-3 py-2 text-left">순번</th>
                <th className="px-3 py-2 text-left">배송요청</th>
                <th className="px-3 py-2 text-left">상품</th>
                <th className="px-3 py-2 text-left">수량</th>
                <th className="px-3 py-2 text-left">수령인</th>
                <th className="px-3 py-2 text-left">상태</th>
                <th className="px-3 py-2 text-left">송장 / 배송완료</th>
                <th className="px-3 py-2 text-left">처리 / 감사카드</th>
                <th className="px-3 py-2 text-left">코드</th>
                <th className="px-3 py-2 text-left">주문자</th>
                {isAdminView && <th className="px-3 py-2 text-left">브랜드</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((d, idx) => {
                // 먼저 들어온(오래된) 주문이 1번. 목록은 최신순이므로 역순으로 번호 부여
                const seq = rows.length - idx;
                const orderer = getOrdererInfo(d);
                const badge = statusLabel[d.deliveryStatus] ?? {
                  text: d.deliveryStatus,
                  className: 'bg-gray-100 text-gray-600',
                };
                const canAdvance = !!nextStatusMap[d.deliveryStatus];
                const canRevert = !!prevStatusMap[d.deliveryStatus];
                const recipient = d.recipient;
                const fullAddress = recipient
                  ? `(${recipient.postalCode}) ${recipient.address} ${
                      recipient.addressDetail || ''
                    }`.trim()
                  : '';
                const requestedAt = formatRelativeDateTime(recipient?.submittedAt);
                const completedAt =
                  d.deliveryStatus === GiftDeliveryStatus.Delivered
                    ? formatRelativeDateTime(d.shipping?.deliveredAt)
                    : null;
                return (
                  <Fragment key={d._id}>
                  <tr className="align-middle hover:bg-gray-50">
                    {/* 1. 순번 */}
                    <td className="whitespace-nowrap px-3 py-2.5 text-sm font-bold text-black">
                      {seq}
                    </td>
                    {/* 2. 배송요청 */}
                    <td className="whitespace-nowrap px-3 py-2.5">
                      {requestedAt ? (
                        <div className="leading-tight">
                          <span
                            className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                              requestedAt.relative === '오늘'
                                ? 'bg-rose-100 text-rose-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {requestedAt.relative}
                          </span>
                          <p className="mt-0.5 text-sm font-bold text-gray-900">
                            {requestedAt.date}{' '}
                            <span className="text-xs font-medium text-gray-400">
                              {requestedAt.time}
                            </span>
                          </p>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-300">-</span>
                      )}
                    </td>
                    {/* 3. 상품 */}
                    <td className="px-3 py-2.5">
                      <p className="max-w-[160px] truncate font-medium text-gray-800">
                        {getItemName(d)}
                      </p>
                      <span className="text-[11px] text-gray-400">
                        {typeLabel[d.type] || d.type}
                      </span>
                    </td>
                    {/* 4. 수량 */}
                    <td className="whitespace-nowrap px-3 py-2.5 text-gray-700">
                      {d.quantity}개
                    </td>
                    {/* 5. 수령인 */}
                    <td className="whitespace-nowrap px-3 py-2.5">
                      <p className="font-medium text-gray-800">
                        {d.recipient?.name || '-'}
                      </p>
                      <p className="text-xs text-gray-400">
                        {formatPhone(d.recipient?.phoneNumber)}
                      </p>
                    </td>
                    {/* 6. 상태 (배지만) */}
                    <td className="whitespace-nowrap px-3 py-2.5">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge.className}`}
                      >
                        {badge.text}
                      </span>
                    </td>
                    {/* 7. 송장 / 배송완료 */}
                    <td className="whitespace-nowrap px-3 py-2.5">
                      {/* 배송완료 건: 완료일 강조 (날짜 + 시각) */}
                      {completedAt && (
                        <p className="mb-1 text-sm font-bold leading-tight text-gray-900">
                          {completedAt.date}{' '}
                          <span className="text-xs font-medium text-gray-400">
                            {completedAt.time}
                          </span>
                        </p>
                      )}
                      {/* 송장/발송 정보 (배송중·배송완료) */}
                      {(d.deliveryStatus === GiftDeliveryStatus.Shipping ||
                        d.deliveryStatus === GiftDeliveryStatus.Delivered) && (
                        <div className="space-y-0.5 text-[11px] leading-tight text-gray-400">
                          {d.shipping?.carrier && (
                            <p className="text-gray-500">{d.shipping.carrier}</p>
                          )}
                          {d.shipping?.trackingNumber ? (
                            <button
                              onClick={() => openShippingEdit(d)}
                              title="송장 정보 수정"
                              className="block font-mono text-gray-500 underline decoration-dotted underline-offset-2 hover:text-gray-700"
                            >
                              {d.shipping.trackingNumber}
                            </button>
                          ) : (
                            <button
                              onClick={() => openShippingEdit(d)}
                              className="font-semibold text-amber-600 underline decoration-dotted underline-offset-2 hover:text-amber-700"
                            >
                              송장 미입력 (입력)
                            </button>
                          )}
                          {formatShortDateTime(d.shipping?.shippedAt) && (
                            <p>발송 {formatShortDateTime(d.shipping?.shippedAt)}</p>
                          )}
                        </div>
                      )}
                      {/* 그 외 상태: 표시할 배송정보 없음 */}
                      {d.deliveryStatus !== GiftDeliveryStatus.Shipping &&
                        d.deliveryStatus !== GiftDeliveryStatus.Delivered && (
                          <span className="text-xs text-gray-300">-</span>
                        )}
                    </td>
                    {/* 7. 처리 / 감사카드 */}
                    <td className="whitespace-nowrap px-3 py-2.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {canAdvance ? (
                          <button
                            onClick={() => advanceStatus(d)}
                            disabled={processingId === d._id}
                            className="rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                          >
                            {processingId === d._id
                              ? '처리중...'
                              : nextStatusLabel[d.deliveryStatus] || '상태 변경'}
                          </button>
                        ) : (
                          !canRevert && (
                            <span className="text-xs text-gray-300">대기</span>
                          )
                        )}
                        {canRevert && (
                          <button
                            onClick={() => revertStatus(d)}
                            disabled={revertingId === d._id}
                            title={prevStatusLabel[d.deliveryStatus]}
                            className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                          >
                            {revertingId === d._id ? '처리중...' : '되돌리기'}
                          </button>
                        )}
                        {d.thanksPrint && (
                          <button
                            onClick={() => setCardDelivery(d)}
                            className="rounded-md border border-rose-200 px-2.5 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50"
                          >
                            감사카드
                          </button>
                        )}
                      </div>
                    </td>
                    {/* 8. 코드 */}
                    <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs text-gray-400">
                      {d.fullCode || d.code || '-'}
                    </td>
                    {/* 9. 주문자 */}
                    <td className="whitespace-nowrap px-3 py-2.5">
                      <p className="text-gray-800">{orderer.name}</p>
                      <p className="text-xs text-gray-400">
                        {formatPhone(orderer.phone)}
                      </p>
                    </td>
                    {/* 10. 브랜드 (admin 전용, 우측 참조열) */}
                    {isAdminView && (
                      <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-600">
                        {typeof d.giftItemBrand === 'object'
                          ? d.giftItemBrand?.name
                          : '-'}
                      </td>
                    )}
                  </tr>
                  {/* 배송지: 두 번째 행으로 분리해 모바일에서 가로로 길게 노출 */}
                  <tr className="hover:bg-gray-50">
                    <td
                      colSpan={isAdminView ? 11 : 10}
                      className="border-t border-dashed border-gray-100 px-3 py-2.5 align-middle"
                    >
                      {recipient?.address ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
                            배송지
                          </span>
                          <p className="min-w-0 text-xs leading-snug text-gray-600">
                            ({recipient.postalCode}) {recipient.address}
                            {recipient.addressDetail
                              ? ` ${recipient.addressDetail}`
                              : ''}
                          </p>
                          <button
                            onClick={() => copyAddress(d._id, fullAddress)}
                            title="주소 복사"
                            className={`shrink-0 rounded-md border px-2 py-1 text-[11px] font-medium transition ${
                              copiedId === d._id
                                ? 'border-green-300 bg-green-50 text-green-600'
                                : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {copiedId === d._id ? '✓ 복사됨' : '주소 복사'}
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
                            배송지
                          </span>
                          <span className="text-xs text-gray-400">미입력</span>
                        </div>
                      )}
                    </td>
                  </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 송장 입력 모달 */}
      {shippingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl">
            <h3 className="mb-4 text-base font-bold">
              {shippingModal.mode === 'edit' ? '송장 정보 수정' : '송장 정보 입력'}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-semibold">택배사</label>
                <input
                  value={shippingModal.carrier}
                  onChange={(e) =>
                    setShippingModal({
                      ...shippingModal,
                      carrier: e.target.value,
                    })
                  }
                  placeholder="예: CJ대한통운"
                  className="w-full rounded border px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold">송장번호</label>
                <input
                  value={shippingModal.trackingNumber}
                  onChange={(e) =>
                    setShippingModal({
                      ...shippingModal,
                      trackingNumber: e.target.value,
                    })
                  }
                  placeholder="송장번호"
                  className="w-full rounded border px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <button
                onClick={() => setShippingModal(null)}
                className="flex-1 rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={submitShipping}
                disabled={processingId === shippingModal.deliveryId}
                className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {shippingModal.mode === 'edit' ? '저장' : '배송중으로 변경'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 배송완료 일시 입력 모달 */}
      {deliverModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl">
            <h3 className="mb-4 text-base font-bold">배송완료 처리</h3>
            <div>
              <label className="mb-1 block text-sm font-semibold">
                배송완료 일시
              </label>
              <input
                type="datetime-local"
                value={deliverModal.deliveredAt}
                onChange={(e) =>
                  setDeliverModal({
                    ...deliverModal,
                    deliveredAt: e.target.value,
                  })
                }
                className="w-full rounded border px-3 py-2 text-sm"
              />
              <p className="mt-1 text-xs text-gray-400">
                기본값은 현재 시각이며, 실제 배송완료 시각으로 수정할 수 있습니다.
              </p>
            </div>
            <div className="mt-5 flex gap-2">
              <button
                onClick={() => setDeliverModal(null)}
                className="flex-1 rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={submitDeliver}
                disabled={processingId === deliverModal.deliveryId}
                className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                배송완료로 변경
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 감사카드 미리보기 / 인쇄 */}
      <GiftBrandThanksCardModal
        delivery={cardDelivery}
        templates={thanksTemplates}
        onClose={() => setCardDelivery(null)}
      />
    </div>
  );
};
